import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  cancelAssetManagerPnl,
  fetchPlatformSettings,
  listAssetManagerPnl,
  listAssetManagerProjects,
  submitAssetManagerPnl,
  updateAssetManagerPnl,
} from "../services/investmentService";
import {
  clampPnlSubmissionDateIso,
  currentNavBdt,
  defaultPnlSubmissionDateIso,
  earliestPnlSubmissionDateIso,
  emptyPnlForm,
  formatBdtAmount,
  formatDecimal,
  formatPnlSubmitError,
  isPnlEligibleStage,
  navAfterPnlBdt,
  netAssetsOnIssuedAfterPnlBdt,
  pendingPnlForProject,
  pnlNavDenominatorShares,
  targetFundAfterPnlBdt,
  totalShareCap,
} from "../utils/assetManagerPnl";
import {
  projectApprovedNetPnlTotal,
  projectFundsRaisedCash,
  projectNetAssetsIssuedAtNav,
  projectTargetFundAtNav,
} from "../utils/investmentPricing";
import { projectTitleWithCode } from "../utils/projectViews";

const AssetManagerWorkspaceContext = createContext(null);

export function AssetManagerWorkspaceProvider({ children }) {
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [pnlRows, setPnlRows] = useState([]);
  const [message, setMessage] = useState("");
  const [pnlSubmitSuccess, setPnlSubmitSuccess] = useState(null);
  const [pnlSubmitError, setPnlSubmitError] = useState("");
  const [adminFeePct, setAdminFeePct] = useState("0");
  const [pnlModalProject, setPnlModalProject] = useState(null);
  const [pnlEditingId, setPnlEditingId] = useState(null);
  const [pnlCancellingId, setPnlCancellingId] = useState(null);
  const [pnlFundValueManual, setPnlFundValueManual] = useState(false);
  const [pnlSubmitting, setPnlSubmitting] = useState(false);
  const [pnlForm, setPnlForm] = useState(() => emptyPnlForm());

  const grossPnl = Number(pnlForm.revenue || 0) - Number(pnlForm.expenses || 0);
  const pctNum = Math.min(100, Math.max(0, Number(adminFeePct || 0)));
  const computedAdminFeeBdt = (grossPnl > 0 ? (grossPnl * pctNum) / 100 : 0).toFixed(2);
  const computedNetPnl = (grossPnl - Number(computedAdminFeeBdt)).toFixed(2);
  const sharePriceAfterSubmissionBdt =
    pnlModalProject == null ? null : navAfterPnlBdt(pnlModalProject, computedNetPnl);
  const netAssetsOnIssuedAfterPnlPreview =
    pnlModalProject == null ? null : netAssetsOnIssuedAfterPnlBdt(pnlModalProject, computedNetPnl);

  const load = useCallback(async () => {
    try {
      const [projectsRes, pnlRes, settingsRes] = await Promise.all([
        listAssetManagerProjects(),
        listAssetManagerPnl(),
        fetchPlatformSettings(),
      ]);
      setAssignedProjects(projectsRes.data || []);
      setPnlRows(pnlRes.data || []);
      setAdminFeePct(String(settingsRes?.data?.pnl_admin_fee_pct ?? "0"));
    } catch {
      setAssignedProjects([]);
      setPnlRows([]);
      setAdminFeePct("0");
    }
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!pnlModalProject) return;
    setPnlForm((prev) => {
      const next = clampPnlSubmissionDateIso(prev.submission_date, pnlModalProject, pnlRows);
      return prev.submission_date === next ? prev : { ...prev, submission_date: next };
    });
  }, [pnlModalProject, pnlRows]);

  useEffect(() => {
    if (!pnlModalProject || pnlFundValueManual) return;
    const nextVal = targetFundAfterPnlBdt(pnlModalProject, computedNetPnl);
    if (nextVal == null) return;
    const next = nextVal.toFixed(2);
    setPnlForm((prev) => (prev.updated_fund_value === next ? prev : { ...prev, updated_fund_value: next }));
  }, [pnlModalProject, computedNetPnl, pnlFundValueManual]);

  const pnlEligibleProjects = useMemo(
    () => assignedProjects.filter((p) => isPnlEligibleStage(p)),
    [assignedProjects]
  );

  const pendingPnlCount = useMemo(
    () => pnlRows.filter((r) => String(r.status || "").toUpperCase() === "PENDING").length,
    [pnlRows]
  );

  const closePnlModal = useCallback(() => {
    setPnlModalProject(null);
    setPnlEditingId(null);
    setPnlFundValueManual(false);
    setPnlSubmitError("");
    setPnlForm(emptyPnlForm());
  }, []);

  const openPnlModal = useCallback(
    async (project) => {
      setMessage("");
      setPnlSubmitSuccess(null);
      setPnlSubmitError("");
      let fresh = assignedProjects.find((p) => String(p.id) === String(project.id)) ?? project;
      try {
        const projectsRes = await listAssetManagerProjects();
        const list = projectsRes.data || [];
        setAssignedProjects(list);
        fresh = list.find((p) => String(p.id) === String(project.id)) ?? fresh;
      } catch {
        /* keep cached */
      }
      setPnlEditingId(null);
      setPnlModalProject(fresh);
      setPnlFundValueManual(false);
      setPnlForm({
        ...emptyPnlForm(),
        project: fresh.id,
        submission_date: defaultPnlSubmissionDateIso(fresh, pnlRows),
      });
    },
    [assignedProjects, pnlRows]
  );

  const openPnlEditModal = useCallback(
    async (row) => {
      setMessage("");
      setPnlSubmitSuccess(null);
      setPnlSubmitError("");
      const project = assignedProjects.find((p) => p.id === row.project) ?? null;
      if (!project) {
        setMessage("Project not found for this P&L row. Refresh and try again.");
        return;
      }
      let fresh = project;
      try {
        const projectsRes = await listAssetManagerProjects();
        const list = projectsRes.data || [];
        setAssignedProjects(list);
        fresh = list.find((p) => String(p.id) === String(project.id)) ?? fresh;
      } catch {
        /* keep cached */
      }
      setPnlEditingId(row.id);
      setPnlModalProject(fresh);
      setPnlFundValueManual(true);
      const submissionDate = String(row.period_end || row.periodEnd || "").slice(0, 10);
      setPnlForm({
        ...emptyPnlForm(),
        project: fresh.id,
        submission_date: submissionDate || defaultPnlSubmissionDateIso(fresh, pnlRows),
        revenue: row.revenue != null ? String(row.revenue) : "",
        expenses: row.expenses != null ? String(row.expenses) : "",
        updated_fund_value: row.updated_fund_value != null ? String(row.updated_fund_value) : "",
        narrative: row.narrative || "",
      });
    },
    [assignedProjects, pnlRows]
  );

  const onCancelPnl = useCallback(
    async (row) => {
      const projectName =
        row.project_title
        || (assignedProjects.find((p) => p.id === row.project)
          ? projectTitleWithCode(assignedProjects.find((p) => p.id === row.project))
          : "this project");
      const ok = window.confirm(
        `Cancel this pending P&L submission for ${projectName}? It will be removed from admin review.`
      );
      if (!ok) return;
      setMessage("");
      setPnlCancellingId(row.id);
      try {
        await cancelAssetManagerPnl(row.id);
        setMessage("P&L submission cancelled.");
        await load();
      } catch (err) {
        setMessage(formatPnlSubmitError(err) || "Failed to cancel P&L.");
      } finally {
        setPnlCancellingId(null);
      }
    },
    [assignedProjects, load]
  );

  const onSubmitPnl = useCallback(
    async (e) => {
      e.preventDefault();
      setMessage("");
      setPnlSubmitError("");
      if (!pnlForm.project) {
        setPnlSubmitError("Project is missing. Close and open Submit P&L from a project card.");
        return;
      }
      if (!pnlForm.submission_date) {
        setPnlSubmitError("Submission date is required.");
        return;
      }
      const projectForMsg = pnlModalProject || assignedProjects.find((p) => p.id === pnlForm.project);
      const earliestIso = projectForMsg ? earliestPnlSubmissionDateIso(projectForMsg, pnlRows) : "";
      if (earliestIso && pnlForm.submission_date < earliestIso) {
        setPnlSubmitError(
          `Submission date must be ${earliestIso} or later (day after the last approved P&L, or fundraising start).`
        );
        return;
      }
      const projectName = projectForMsg ? projectTitleWithCode(projectForMsg) : "Project";
      const netPnlLabel = `BDT ${Number(computedNetPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const payload = {
        project: pnlForm.project,
        submission_date: pnlForm.submission_date,
        revenue: String(pnlForm.revenue || "0"),
        expenses: String(pnlForm.expenses || "0"),
        narrative: pnlForm.narrative || "",
      };
      const fv = String(pnlForm.updated_fund_value ?? "").trim();
      if (fv !== "") payload.updated_fund_value = fv;

      setPnlSubmitting(true);
      try {
        if (pnlEditingId) {
          await updateAssetManagerPnl(pnlEditingId, payload);
        } else {
          await submitAssetManagerPnl(payload);
        }
        setPnlSubmitSuccess({
          title: pnlEditingId ? "P&L updated" : "P&L submitted",
          projectName,
          netPnlLabel,
          message: pnlEditingId
            ? "Your revised submission is pending admin review."
            : "Your submission is pending admin review.",
        });
        closePnlModal();
        load().catch(() => {});
      } catch (err) {
        setPnlSubmitError(formatPnlSubmitError(err));
      } finally {
        setPnlSubmitting(false);
      }
    },
    [
      pnlForm,
      pnlModalProject,
      assignedProjects,
      pnlRows,
      computedNetPnl,
      pnlEditingId,
      closePnlModal,
      load,
    ]
  );

  const value = {
    assignedProjects,
    pnlRows,
    message,
    setMessage,
    pnlSubmitSuccess,
    setPnlSubmitSuccess,
    pnlSubmitError,
    adminFeePct,
    pnlModalProject,
    pnlEditingId,
    pnlCancellingId,
    pnlFundValueManual,
    setPnlFundValueManual,
    pnlSubmitting,
    pnlForm,
    setPnlForm,
    grossPnl,
    computedAdminFeeBdt,
    computedNetPnl,
    sharePriceAfterSubmissionBdt,
    netAssetsOnIssuedAfterPnlPreview,
    load,
    pnlEligibleProjects,
    pendingPnlCount,
    pendingPnlForProject: (projectId) => pendingPnlForProject(projectId, pnlRows),
    closePnlModal,
    openPnlModal,
    openPnlEditModal,
    onCancelPnl,
    onSubmitPnl,
    projectMetrics: {
      projectApprovedNetPnlTotal,
      projectFundsRaisedCash,
      projectNetAssetsIssuedAtNav,
      projectTargetFundAtNav,
      formatDecimal,
      formatBdtAmount,
      currentNavBdt,
      pnlNavDenominatorShares,
      totalShareCap,
      earliestPnlSubmissionDateIso: (p) => earliestPnlSubmissionDateIso(p, pnlRows),
      clampPnlSubmissionDateIso: (v, p) => clampPnlSubmissionDateIso(v, p, pnlRows),
      targetFundAfterPnlBdt,
    },
  };

  return (
    <AssetManagerWorkspaceContext.Provider value={value}>{children}</AssetManagerWorkspaceContext.Provider>
  );
}

export function useAssetManagerWorkspace() {
  const ctx = useContext(AssetManagerWorkspaceContext);
  if (!ctx) {
    throw new Error("useAssetManagerWorkspace must be used within AssetManagerWorkspaceProvider");
  }
  return ctx;
}
